// Shared helpers for the new "features + per-tier pricing" mapping model.
//
// The Create modal saves a mapping as:
//   { questionnaireId, answerCombination, featuresByTier, tierPricing, active }
// where
//   featuresByTier = { low: [featureId], mid: [...], high: [...] }
//   tierPricing    = { [durationId]: { [countryId]: { low: {finalPrice, priceBeforeDiscount, discountType, discountValue}, mid, high } } }
//
// The backend may echo these at the top level or nest them under `meta`,
// so reads go through readTierConfig() to stay tolerant of either shape.

const EMPTY_TIERS = { low: [], mid: [], high: [] };

// Questions branch via each answer option's `nextQuestionId`, so their real
// display sequence to the end user is a tree path, not their static
// `orderIndex` (which only orders siblings, not across branches). The root
// is whichever question no other question's answer points to.
export function findRootQuestion(questions) {
  const live = activeQuestions(questions);
  const allNextIds = new Set(
    live.flatMap(q => (q.answerOptions || []).map(a => a.nextQuestionId).filter(Boolean))
  );
  const sorted = [...live].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  return sorted.find(q => !allNextIds.has(q.id)) || sorted[0];
}

// Walk the question tree from the root, following whichever answer this
// combination selected at each step, to recover the true question order for
// one specific answerCombination (edit mode / display).
export function buildPathFromAnswers(questions, answerCombination) {
  const path = [];
  const visited = new Set();
  const root = findRootQuestion(questions);
  if (!root) return path;

  let currentQId = root.id;
  while (currentQId && !visited.has(currentQId)) {
    visited.add(currentQId);
    const q = questions.find(x => x.id === currentQId);
    if (!q) break;
    path.push(currentQId);

    const selectedIds = answerCombination[currentQId] || [];
    const firstAnswerId = Array.isArray(selectedIds) ? selectedIds[0] : selectedIds;
    if (!firstAnswerId) break;

    const answerOpt = q.answerOptions?.find(a => a.id === firstAnswerId);
    currentQId = answerOpt?.nextQuestionId || null;
  }
  return path;
}

// Only active questions take part in the flow — a deactivated question no
// option points to would otherwise be picked up as the root.
export const activeQuestions = (questions) => (questions || []).filter(q => q.isActive !== false);

// Step number of every question in the end-user flow: the fewest
// nextQuestionId hops from the root (1-based). A question several branches
// lead to keeps its earliest step, so "Step 7" means the 7th question the
// user sees whichever branch they took.
export function questionSteps(questions) {
  const qs = activeQuestions(questions);
  const byId = new Map(qs.map(q => [q.id, q]));
  const root = findRootQuestion(qs);
  const steps = {};
  if (!root) return steps;
  let frontier = [root.id];
  steps[root.id] = 1;
  while (frontier.length) {
    const next = [];
    for (const id of frontier) {
      for (const opt of byId.get(id)?.answerOptions || []) {
        const nid = opt.nextQuestionId;
        if (nid && byId.has(nid) && steps[nid] === undefined) {
          steps[nid] = steps[id] + 1;
          next.push(nid);
        }
      }
    }
    frontier = next;
  }
  return steps;
}

// Question ids sorted the way the user meets them (step, then orderIndex);
// ids the flow never reaches go last.
export function orderByFlow(questions, questionIds) {
  const steps = questionSteps(questions);
  const orderIndex = Object.fromEntries((questions || []).map(q => [q.id, q.orderIndex || 0]));
  const rank = id => (steps[id] === undefined ? Infinity : steps[id]);
  return [...questionIds].sort((a, b) => rank(a) - rank(b) || (orderIndex[a] || 0) - (orderIndex[b] || 0));
}

export function readTierConfig(mapping) {
  if (!mapping) return { featuresByTier: { ...EMPTY_TIERS }, tierPricing: {} };

  const featuresByTier =
    mapping.featuresByTier ||
    mapping.meta?.featuresByTier ||
    { ...EMPTY_TIERS };

  const tierPricing =
    mapping.tierPricing ||
    mapping.meta?.tierPricing ||
    {};

  return {
    featuresByTier: {
      low:  Array.isArray(featuresByTier.low)  ? featuresByTier.low  : [],
      mid:  Array.isArray(featuresByTier.mid)  ? featuresByTier.mid  : [],
      high: Array.isArray(featuresByTier.high) ? featuresByTier.high : [],
    },
    tierPricing: tierPricing && typeof tierPricing === 'object' ? tierPricing : {},
  };
}

// Number of features assigned to a tier.
export function tierFeatureCount(mapping, tier) {
  return readTierConfig(mapping).featuresByTier[tier]?.length || 0;
}

// All price cells for a tier, flattened across every duration/country combination.
// Returns: [{ durationId, countryId, finalPrice, priceBeforeDiscount, discountType, discountValue }]
export function tierPriceCells(mapping, tier) {
  const { tierPricing } = readTierConfig(mapping);
  const cells = [];
  for (const [durationId, byCountry] of Object.entries(tierPricing)) {
    if (!byCountry || typeof byCountry !== 'object') continue;
    for (const [countryId, byTier] of Object.entries(byCountry)) {
      const cell = byTier?.[tier];
      if (cell && (cell.finalPrice !== undefined && cell.finalPrice !== '' && cell.finalPrice !== null)) {
        cells.push({ durationId, countryId, ...cell });
      }
    }
  }
  return cells;
}

// True if the tier has any assigned features or configured prices.
export function tierHasContent(mapping, tier) {
  return tierFeatureCount(mapping, tier) > 0 || tierPriceCells(mapping, tier).length > 0;
}

// Selected add-on ids — tolerant of top-level or meta-nested storage.
export function readAddOnIds(mapping) {
  if (!mapping) return [];
  const ids = mapping.addOnIds || mapping.meta?.addOnIds || [];
  return Array.isArray(ids) ? ids : [];
}

// Per-tier display copy (title / subtitle / description) shown on the
// user-facing plan cards. Optional — tolerant of top-level or meta-nested
// storage, and always returns a complete { low, mid, high } shape so callers
// can read fields without guards. Empty strings mean "use the default".
const EMPTY_COPY = { title: '', subtitle: '', description: '' };

export function readTierCopy(mapping) {
  const blank = () => ({ low: { ...EMPTY_COPY }, mid: { ...EMPTY_COPY }, high: { ...EMPTY_COPY } });
  if (!mapping) return blank();
  const src = mapping.tierCopy || mapping.meta?.tierCopy || {};
  const pick = (tier) => ({
    title:       src?.[tier]?.title || '',
    subtitle:    src?.[tier]?.subtitle || '',
    description: src?.[tier]?.description || '',
  });
  return { low: pick('low'), mid: pick('mid'), high: pick('high') };
}
